import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { 
  Calendar, 
  Package, 
  ShoppingBag, 
  Settings, 
  Plus, 
  Save, 
  Trash2,
  Sun,
  Snowflake,
  Leaf,
  Cloud,
  Edit,
  Check,
  X
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Season icons
const SEASON_ICONS = {
  'Jan': <Snowflake className="h-4 w-4 text-blue-400" />,
  'Feb': <Snowflake className="h-4 w-4 text-blue-400" />,
  'Mar': <Leaf className="h-4 w-4 text-green-500" />,
  'Apr': <Leaf className="h-4 w-4 text-green-500" />,
  'May': <Sun className="h-4 w-4 text-yellow-500" />,
  'Jun': <Sun className="h-4 w-4 text-yellow-500" />,
  'Jul': <Sun className="h-4 w-4 text-orange-500" />,
  'Aug': <Sun className="h-4 w-4 text-orange-500" />,
  'Sep': <Leaf className="h-4 w-4 text-orange-400" />,
  'Oct': <Leaf className="h-4 w-4 text-orange-400" />,
  'Nov': <Cloud className="h-4 w-4 text-gray-400" />,
  'Dec': <Snowflake className="h-4 w-4 text-blue-400" />,
};

const ALL_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const SERVICE_TYPES = [
  { id: 'setup_installation', name: 'Tent Setup & Pack-down', icon: '⛺' },
  { id: 'rental', name: 'Camping Gear Rental', icon: '🎒' },
  { id: 'guided_experience', name: 'Guided Camping Trip', icon: '🧭' },
  { id: 'safety_check', name: 'Camping Safety Check', icon: '🛡️' },
  { id: 'trip_planning', name: 'Trip Planning', icon: '🗺️' },
];

/**
 * Admin component for managing Outdoor & Camping features:
 * - Seasonal availability settings
 * - Bundle management
 * - Product-to-service mapping rules
 */
const AdminOutdoorCamping = ({ country = 'SL' }) => {
  const [activeTab, setActiveTab] = useState('seasonal');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Seasonal settings state
  const [seasonalSettings, setSeasonalSettings] = useState({
    enabled: true,
    services: {},
  });
  
  // Bundles state
  const [bundles, setBundles] = useState([]);
  const [editingBundle, setEditingBundle] = useState(null);
  const [showBundleDialog, setShowBundleDialog] = useState(false);
  
  // Product mapping state
  const [productMappings, setProductMappings] = useState([]);
  const [editingMapping, setEditingMapping] = useState(null);
  const [showMappingDialog, setShowMappingDialog] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [seasonalRes, bundlesRes, mappingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/sports-services/admin/seasonal-settings/outdoor-camping`, {
          params: { country }
        }),
        axios.get(`${API_URL}/api/sports-services/bundles/outdoor-camping`, {
          params: { country }
        }),
        axios.get(`${API_URL}/api/sports-services/admin/product-mappings/outdoor-camping`, {
          params: { country }
        }),
      ]);
      
      setSeasonalSettings(seasonalRes.data);
      setBundles(bundlesRes.data.bundles || []);
      setProductMappings(mappingsRes.data.mappings || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [country]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ============ Seasonal Settings ============
  const handleSeasonalToggle = (enabled) => {
    setSeasonalSettings(prev => ({ ...prev, enabled }));
  };

  const handleMonthToggle = (serviceType, month) => {
    setSeasonalSettings(prev => {
      const currentMonths = prev.services?.[serviceType]?.allowed_months || [];
      const newMonths = currentMonths.includes(month)
        ? currentMonths.filter(m => m !== month)
        : [...currentMonths, month];
      
      return {
        ...prev,
        services: {
          ...prev.services,
          [serviceType]: {
            ...prev.services?.[serviceType],
            allowed_months: newMonths,
          }
        }
      };
    });
  };

  const saveSeasonalSettings = async () => {
    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/sports-services/admin/seasonal-settings/outdoor-camping`, {
        country,
        settings: seasonalSettings,
      });
      toast.success('Seasonal settings saved!');
    } catch (err) {
      console.error('Error saving seasonal settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // ============ Bundle Management ============
  const handleSaveBundle = async () => {
    try {
      setSaving(true);
      if (editingBundle?.id) {
        await axios.put(`${API_URL}/api/sports-services/admin/bundles/${editingBundle.id}`, {
          ...editingBundle,
          country,
        });
        toast.success('Bundle updated!');
      } else {
        await axios.post(`${API_URL}/api/sports-services/admin/bundles`, {
          ...editingBundle,
          country,
          subcategory: 'outdoor-camping',
        });
        toast.success('Bundle created!');
      }
      setShowBundleDialog(false);
      setEditingBundle(null);
      fetchData();
    } catch (err) {
      console.error('Error saving bundle:', err);
      toast.error('Failed to save bundle');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBundle = async (bundleId) => {
    if (!confirm('Are you sure you want to delete this bundle?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/sports-services/admin/bundles/${bundleId}`);
      toast.success('Bundle deleted');
      fetchData();
    } catch (err) {
      console.error('Error deleting bundle:', err);
      toast.error('Failed to delete bundle');
    }
  };

  // ============ Product Mapping ============
  const handleSaveMapping = async () => {
    try {
      setSaving(true);
      if (editingMapping?.id) {
        await axios.put(`${API_URL}/api/sports-services/admin/product-mappings/${editingMapping.id}`, {
          ...editingMapping,
          country,
        });
        toast.success('Mapping updated!');
      } else {
        await axios.post(`${API_URL}/api/sports-services/admin/product-mappings`, {
          ...editingMapping,
          country,
          subcategory: 'outdoor-camping',
        });
        toast.success('Mapping created!');
      }
      setShowMappingDialog(false);
      setEditingMapping(null);
      fetchData();
    } catch (err) {
      console.error('Error saving mapping:', err);
      toast.error('Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMapping = async (mappingId) => {
    if (!confirm('Are you sure you want to delete this mapping?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/sports-services/admin/product-mappings/${mappingId}`);
      toast.success('Mapping deleted');
      fetchData();
    } catch (err) {
      console.error('Error deleting mapping:', err);
      toast.error('Failed to delete mapping');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-outdoor-camping">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            🏕️ Outdoor & Camping Settings
          </h2>
          <p className="text-sm text-gray-500">
            Manage seasonal availability, bundles, and product mappings for {country}
          </p>
        </div>
        <Badge variant="outline" className="text-sm">
          {country === 'SL' ? '🇱🇰 Sri Lanka' : '🇬🇧 United Kingdom'}
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="seasonal" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Seasonal
          </TabsTrigger>
          <TabsTrigger value="bundles" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Bundles
          </TabsTrigger>
          <TabsTrigger value="mappings" className="flex items-center gap-2">
            <ShoppingBag className="h-4 w-4" />
            Product Mapping
          </TabsTrigger>
        </TabsList>

        {/* ============ Seasonal Settings Tab ============ */}
        <TabsContent value="seasonal" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Seasonal Availability</CardTitle>
                <div className="flex items-center gap-2">
                  <Label htmlFor="seasonal-toggle">Enable Seasonal Logic</Label>
                  <Switch
                    id="seasonal-toggle"
                    checked={seasonalSettings.enabled}
                    onCheckedChange={handleSeasonalToggle}
                    data-testid="seasonal-toggle"
                  />
                </div>
              </div>
              <p className="text-sm text-gray-500">
                Control which months each service is available. Services are hidden when not in season.
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {SERVICE_TYPES.map(service => (
                  <div key={service.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{service.icon}</span>
                        <span className="font-medium">{service.name}</span>
                      </div>
                      <Badge variant="outline">
                        {(seasonalSettings.services?.[service.id]?.allowed_months || []).length}/12 months
                      </Badge>
                    </div>
                    
                    {/* Month grid */}
                    <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
                      {ALL_MONTHS.map(month => {
                        const isActive = (seasonalSettings.services?.[service.id]?.allowed_months || []).includes(month);
                        return (
                          <button
                            key={month}
                            onClick={() => handleMonthToggle(service.id, month)}
                            disabled={!seasonalSettings.enabled}
                            className={`p-2 rounded-lg border text-center transition-all ${
                              isActive 
                                ? 'bg-green-100 border-green-300 text-green-700' 
                                : 'bg-gray-50 border-gray-200 text-gray-400'
                            } ${!seasonalSettings.enabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-50'}`}
                            data-testid={`month-toggle-${service.id}-${month}`}
                          >
                            <div className="flex flex-col items-center gap-1">
                              {SEASON_ICONS[month]}
                              <span className="text-xs font-medium">{month}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                
                <Button 
                  onClick={saveSeasonalSettings} 
                  disabled={saving}
                  className="w-full"
                  data-testid="save-seasonal-settings"
                >
                  {saving ? 'Saving...' : 'Save Seasonal Settings'}
                  <Save className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ Bundles Tab ============ */}
        <TabsContent value="bundles" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Bundle Management</CardTitle>
                <Dialog open={showBundleDialog} onOpenChange={setShowBundleDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      onClick={() => setEditingBundle({
                        name: '',
                        description: '',
                        icon: '🏕️',
                        discount_percent: 15,
                        included_services: [],
                        original_price: 0,
                        final_price: 0,
                        is_active: true,
                      })}
                      data-testid="add-bundle-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Bundle
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>
                        {editingBundle?.id ? 'Edit Bundle' : 'Create Bundle'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Bundle Name</Label>
                        <Input
                          value={editingBundle?.name || ''}
                          onChange={e => setEditingBundle(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="e.g., Beginner Camping Bundle"
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Input
                          value={editingBundle?.description || ''}
                          onChange={e => setEditingBundle(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Short description"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Icon</Label>
                          <Select 
                            value={editingBundle?.icon || '🏕️'}
                            onValueChange={v => setEditingBundle(prev => ({ ...prev, icon: v }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="🏕️">🏕️ Tent</SelectItem>
                              <SelectItem value="🎒">🎒 Backpack</SelectItem>
                              <SelectItem value="⛺">⛺ Camp</SelectItem>
                              <SelectItem value="🧭">🧭 Compass</SelectItem>
                              <SelectItem value="🔥">🔥 Campfire</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Discount %</Label>
                          <Input
                            type="number"
                            min="0"
                            max="50"
                            value={editingBundle?.discount_percent || 0}
                            onChange={e => setEditingBundle(prev => ({ ...prev, discount_percent: parseInt(e.target.value) || 0 }))}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Original Price</Label>
                          <Input
                            type="number"
                            min="0"
                            value={editingBundle?.original_price || 0}
                            onChange={e => setEditingBundle(prev => ({ ...prev, original_price: parseInt(e.target.value) || 0 }))}
                          />
                        </div>
                        <div>
                          <Label>Final Price</Label>
                          <Input
                            type="number"
                            min="0"
                            value={editingBundle?.final_price || 0}
                            onChange={e => setEditingBundle(prev => ({ ...prev, final_price: parseInt(e.target.value) || 0 }))}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Included Services</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {SERVICE_TYPES.map(service => {
                            const isIncluded = (editingBundle?.included_services || []).includes(service.id);
                            return (
                              <button
                                key={service.id}
                                onClick={() => setEditingBundle(prev => ({
                                  ...prev,
                                  included_services: isIncluded
                                    ? (prev.included_services || []).filter(s => s !== service.id)
                                    : [...(prev.included_services || []), service.id]
                                }))}
                                className={`px-3 py-1 rounded-full text-sm border transition-all ${
                                  isIncluded 
                                    ? 'bg-green-100 border-green-300 text-green-700' 
                                    : 'bg-gray-50 border-gray-200 text-gray-500'
                                }`}
                              >
                                {service.icon} {service.name.split(' ')[0]}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>Active</Label>
                        <Switch
                          checked={editingBundle?.is_active ?? true}
                          onCheckedChange={v => setEditingBundle(prev => ({ ...prev, is_active: v }))}
                        />
                      </div>
                      <Button onClick={handleSaveBundle} disabled={saving} className="w-full">
                        {saving ? 'Saving...' : 'Save Bundle'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {bundles.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  No bundles created yet. Click &quot;Add Bundle&quot; to create one.
                </p>
              ) : (
                <div className="space-y-4">
                  {bundles.map(bundle => (
                    <div 
                      key={bundle.id} 
                      className="border rounded-lg p-4 flex items-center justify-between"
                      data-testid={`bundle-item-${bundle.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{bundle.icon}</span>
                        <div>
                          <h4 className="font-medium">{bundle.name}</h4>
                          <p className="text-sm text-gray-500">{bundle.description}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {bundle.discount_percent}% off
                            </Badge>
                            <span className="text-xs text-gray-400">
                              {bundle.included_services?.length || 0} services
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingBundle(bundle);
                            setShowBundleDialog(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteBundle(bundle.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ Product Mapping Tab ============ */}
        <TabsContent value="mappings" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Product-to-Service Mapping</CardTitle>
                  <p className="text-sm text-gray-500">
                    Define which services to suggest when a user views/buys specific products
                  </p>
                </div>
                <Dialog open={showMappingDialog} onOpenChange={setShowMappingDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      onClick={() => setEditingMapping({
                        product_type: '',
                        trigger: 'view', // 'view', 'checkout', 'delivery'
                        suggested_services: [],
                        prompt_title: '',
                        prompt_subtitle: '',
                        cta_text: 'Add service',
                        is_active: true,
                      })}
                      data-testid="add-mapping-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Mapping
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>
                        {editingMapping?.id ? 'Edit Mapping' : 'Create Mapping'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Product Type</Label>
                        <Select 
                          value={editingMapping?.product_type || ''}
                          onValueChange={v => setEditingMapping(prev => ({ ...prev, product_type: v }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select product type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="tent">Tent</SelectItem>
                            <SelectItem value="large_tent">Large/Group Tent</SelectItem>
                            <SelectItem value="family_tent">Family Tent</SelectItem>
                            <SelectItem value="sleeping_bag">Sleeping Bag</SelectItem>
                            <SelectItem value="camping_stove">Camping Stove</SelectItem>
                            <SelectItem value="camping_bundle">Camping Bundle</SelectItem>
                            <SelectItem value="hiking_gear">Hiking Gear</SelectItem>
                            <SelectItem value="full_camping_kit">Full Camping Kit</SelectItem>
                            <SelectItem value="kids_gear">Kids Camping Gear</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Trigger Point</Label>
                        <Select 
                          value={editingMapping?.trigger || 'view'}
                          onValueChange={v => setEditingMapping(prev => ({ ...prev, trigger: v }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="view">Product View</SelectItem>
                            <SelectItem value="checkout">Post-Checkout</SelectItem>
                            <SelectItem value="delivery">Post-Delivery</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Prompt Title</Label>
                        <Input
                          value={editingMapping?.prompt_title || ''}
                          onChange={e => setEditingMapping(prev => ({ ...prev, prompt_title: e.target.value }))}
                          placeholder="e.g., Your tent is on the way!"
                        />
                      </div>
                      <div>
                        <Label>Prompt Subtitle</Label>
                        <Input
                          value={editingMapping?.prompt_subtitle || ''}
                          onChange={e => setEditingMapping(prev => ({ ...prev, prompt_subtitle: e.target.value }))}
                          placeholder="e.g., Need help setting it up?"
                        />
                      </div>
                      <div>
                        <Label>CTA Text</Label>
                        <Input
                          value={editingMapping?.cta_text || ''}
                          onChange={e => setEditingMapping(prev => ({ ...prev, cta_text: e.target.value }))}
                          placeholder="e.g., Add setup service"
                        />
                      </div>
                      <div>
                        <Label>Suggested Services</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {SERVICE_TYPES.map(service => {
                            const isIncluded = (editingMapping?.suggested_services || []).includes(service.id);
                            return (
                              <button
                                key={service.id}
                                onClick={() => setEditingMapping(prev => ({
                                  ...prev,
                                  suggested_services: isIncluded
                                    ? (prev.suggested_services || []).filter(s => s !== service.id)
                                    : [...(prev.suggested_services || []), service.id]
                                }))}
                                className={`px-3 py-1 rounded-full text-sm border transition-all ${
                                  isIncluded 
                                    ? 'bg-blue-100 border-blue-300 text-blue-700' 
                                    : 'bg-gray-50 border-gray-200 text-gray-500'
                                }`}
                              >
                                {service.icon} {service.name.split(' ')[0]}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>Active</Label>
                        <Switch
                          checked={editingMapping?.is_active ?? true}
                          onCheckedChange={v => setEditingMapping(prev => ({ ...prev, is_active: v }))}
                        />
                      </div>
                      <Button onClick={handleSaveMapping} disabled={saving} className="w-full">
                        {saving ? 'Saving...' : 'Save Mapping'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {productMappings.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  No product mappings created yet. Click &quot;Add Mapping&quot; to create one.
                </p>
              ) : (
                <div className="space-y-4">
                  {productMappings.map(mapping => (
                    <div 
                      key={mapping.id} 
                      className="border rounded-lg p-4"
                      data-testid={`mapping-item-${mapping.id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium capitalize">
                            {mapping.product_type?.replace(/_/g, ' ') || 'Unknown'}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {mapping.trigger === 'view' && '👁️ On View'}
                            {mapping.trigger === 'checkout' && '🛒 Post-Checkout'}
                            {mapping.trigger === 'delivery' && '📦 Post-Delivery'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingMapping(mapping);
                              setShowMappingDialog(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteMapping(mapping.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 mb-2">
                        &quot;{mapping.prompt_title}&quot; - &quot;{mapping.prompt_subtitle}&quot;
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {(mapping.suggested_services || []).map(serviceId => {
                          const service = SERVICE_TYPES.find(s => s.id === serviceId);
                          return (
                            <Badge key={serviceId} variant="secondary" className="text-xs">
                              {service?.icon} {service?.name.split(' ')[0]}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminOutdoorCamping;
